import { HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	camelToSnake,
	IQueryParams,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Map as ImMap, Set } from 'immutable';
import { map, Observable } from 'rxjs';

import { DcCoreElementType } from '../../core/dc-core-types';
import { ElementPermission } from '../../core/domain/permission-types';
import { DxFilterField, DxSortField } from '../../core/dx-types';
import { DxGridUtil } from '../../core/utils/dx-grid.util';
import { MemberAccessEntity } from '../../project-subscription/components/subscription-view/permissions-config-modal/member-access.entity';
import { ColumnConfigItemEntity } from '../../project-subscription/domain/column-config-item.entity';
import { ColumnModelItemEntity } from '../../project-subscription/domain/column-model-item.entity';
import { FilterModelItemEntity } from '../../project-subscription/domain/filter-model-item.entity';
import { InternalMetadataEntity } from '../../project-subscription/domain/internal-metadata.entity';
import { ProfileItemEntity } from '../../project-subscription/domain/profile-item.entity';
import { PublicMetadataEntity } from '../../project-subscription/domain/public-metadata.entity';
import {
	FilterVersion,
	SimulationDataSource,
	SimulationSortColumnType,
} from '../../project-subscription/domain/subscription.types';
import { RuleMapperService } from '../../project-subscription/subscription-filter.adapter';
import { DcBaseRequester } from '../core/dc-base.requester';
import { IDcMetadataResponse } from '../core/models/metadata.model';
import { IProjectModel } from '../core/models/project.model';
import { IConsumerCreate } from './models/consumer-create.model';
import {
	IMembersPermissionsUpdate,
	ISubscriptionMemberAccess,
} from './models/permission.model';
import { IPublicMetadata } from './models/public-metadata.model';
import {
	ISimulationPayload,
	ParsedSimulationResult,
	SimulationDataSourceEnum,
	SimulationResult,
	SimulationSortColumn,
} from './models/simulator.model';
import { ISubscriptionColumnConfig } from './models/subscription-column-config.model';
import { ISubscriptionCreate } from './models/subscription-create.model';
import {
	IJobTracking,
	ISubscriptionHistory,
} from './models/subscription-history.model';
import {
	IColumnTemplate,
	IFilterTemplate,
	IProfileMetadata,
	ISubscriptionProfile,
	ISubscriptionProfileColumn,
} from './models/subscription-profile.model';
import {
	IConsumedProfileColumn,
	ISubscriptionProjectModel,
	SubscriptionFilterVersion,
} from './models/subscription-project.model';
import {
	FilterPayloadReq,
	ISubscriptionResponse,
	RuleFilterPayloadReq,
	SubscriptionFields,
	UsableSubscriptionResp,
} from './models/subscription.model';
import {
	ISwitchableProjectModel,
	ISwitchVersionResponseModel,
	VersionSwitchModel,
} from './models/version-switch.model';

@Injectable({
	providedIn: 'root',
})
export class SubscriptionRequester extends DcBaseRequester<ISubscriptionResponse> {
	protected resource;
	protected elementsListUrl;
	protected singleElementUrl;

	private readonly subscriptionFieldMapping: {
		[k: string]: SubscriptionFields;
	} = {
		label: SubscriptionFields.Label,
		description: SubscriptionFields.Description,
		createdBy: SubscriptionFields.CreatedBy,
		updatedBy: SubscriptionFields.UpdatedBy,
		datablockSource: SubscriptionFields.DatablockLabel,
		datablockId: SubscriptionFields.DatablockId,
		status: SubscriptionFields.Status,
		tags: SubscriptionFields.Tags,
	};

	private readonly filterVersionMapping: Record<
		FilterVersion,
		SubscriptionFilterVersion
	> = {
		[FilterVersion.CurrentVersion]: 'THIS_VERSION',
		[FilterVersion.AllVersions]: 'ALL',
		[FilterVersion.OtherVersions]: 'OTHER_VERSIONS',
	};

	public constructor(
		private readonly urlService: UrlService,
		private readonly ruleMapperService: RuleMapperService
	) {
		super();
		this.resource = this.generateResourceUrl('DATALINKS');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = `${this.resource}/{id}`;
	}

	public getSubscriptions(
		pageSize: number,
		pageIndex: number,
		search: string,
		filteredFields: Array<DxFilterField>,
		sortedFields: Array<DxSortField>,
		excludeInactive = false,
		withPermissions = false
	): Observable<{
		elements: ReadonlyArray<ISubscriptionResponse>;
		total: number;
	}> {
		const filter = DxGridUtil.generateFilterFieldPayload(
			filteredFields,
			this.subscriptionFieldMapping
		);

		const sortParams = DxGridUtil.generateSortFieldPayload(
			sortedFields,
			this.subscriptionFieldMapping
		);

		const defaultQueryParams: IQueryParams = {
			page: pageIndex,
			size: pageSize,
			...filter,
			withPermissions,
		};

		const queryParams: IQueryParams = search
			? {
					...defaultQueryParams,
					['fullText.contains']: search,
			  }
			: defaultQueryParams;

		let params = new HttpParams({
			fromObject: queryParams as {
				[key: string]:
					| number
					| string
					| boolean
					| Array<number | string | boolean>;
			},
		});

		if (sortParams && sortParams.length > 0) {
			sortParams.forEach((s) => {
				params = params.append('sort', s);
			});
		}

		const url = this.urlService.generateUrl(
			this.resource,
			{},
			{
				project: this.projectId,
				excludeInactive,
			}
		);

		return this.http
			.get<ReadonlyArray<ISubscriptionResponse>>(url, {
				params,
				observe: 'response',
			})
			.pipe(
				map((response) => ({
					elements: response.body ?? [],
					total: this.getTotal(response),
				}))
			);
	}

	public getUsableSubscriptions(): Observable<Array<UsableSubscriptionResp>> {
		const resourceUrl = this.generateResourceUrl('USABLE_DATALINKS');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
			}
		);
		return this.http.get<Array<UsableSubscriptionResp>>(url);
	}

	public createSubscription(
		datablockId: number,
		internalMetadata: InternalMetadataEntity,
		publicMetadata: PublicMetadataEntity,
		columns: ImMap<string, ColumnConfigItemEntity>,
		profile: ProfileItemEntity,
		columnModel: ColumnModelItemEntity,
		filterModels: Array<FilterModelItemEntity>
	): Observable<ISubscriptionResponse> {
		const body = this.generateSubscriptionPayloadForSave(
			datablockId,
			internalMetadata,
			publicMetadata,
			columns,
			profile,
			columnModel,
			filterModels
		);
		const url = this.urlService.generateUrl(
			this.resource,
			{},
			{
				project: this.projectId,
			}
		);
		return this.http.post<ISubscriptionResponse>(url, body);
	}

	public deleteSubscription(subscriptionId: number): Observable<void> {
		const url = this.urlService.generateUrl(
			this.singleElementUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.delete<void>(url);
	}

	public getSubscription(
		subscriptionId: number,
		useCache = true
	): Observable<ISubscriptionResponse> {
		const url = this.urlService.generateUrl(
			this.singleElementUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);

		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  });
		return this.get<ISubscriptionResponse>(url, cachePolicy);
	}

	public updateInternalMetadata(
		subscriptionId: number,
		internalMetadata: InternalMetadataEntity
	): Observable<IDcMetadataResponse> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_METADATA');
		const body = this.generateInternalMetadataPayload(internalMetadata);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.put<IDcMetadataResponse>(url, body);
	}

	public updatePublicMetadata(
		subscriptionId: number,
		publicMetadata: PublicMetadataEntity
	): Observable<IPublicMetadata> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_PUBLIC_METADATA');
		const body = this.generatePublicMetadataPayload(publicMetadata);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.put<IPublicMetadata>(url, body);
	}

	public getSubscriptionColumns(
		subscriptionId: number,
		useCache = true
	): Observable<Array<ISubscriptionColumnConfig>> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_COLUMNS');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);

		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.CacheOnly,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  });
		return this.get<Array<ISubscriptionColumnConfig>>(url, cachePolicy);
	}

	public updateSubscriptionColumns(
		subscriptionId: number,
		columns: ImMap<string, ColumnConfigItemEntity>
	): Observable<Array<ISubscriptionColumnConfig>> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_COLUMNS');
		const body = this.generateSubscriptionColumnsPayload(columns);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.put<Array<ISubscriptionColumnConfig>>(url, body);
	}

	public getSubscriptionProfiles(
		subscriptionId: number,
		useCache = true
	): Observable<Array<ISubscriptionProfile>> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_PROFILES');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);

		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  });

		return this.get<Array<ISubscriptionProfile>>(url, cachePolicy);
	}

	public createSubscriptionProfile(
		subscriptionId: number,
		profile: ProfileItemEntity,
		columnModel: ColumnModelItemEntity | string,
		filterModels: Array<FilterModelItemEntity | string>
	): Observable<ISubscriptionProfile> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_PROFILES');
		const body = this.generateSubscriptionProfilePayload(
			profile,
			columnModel,
			filterModels
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.post<ISubscriptionProfile>(url, body);
	}

	public updateProfile(
		subscriptionId: number,
		profile: ProfileItemEntity,
		columnModel: ColumnModelItemEntity | string,
		filterModels: Array<FilterModelItemEntity | string>
	): Observable<ISubscriptionProfile> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_PROFILE_BY_ID');
		const body = this.generateSubscriptionProfilePayload(
			profile,
			columnModel,
			filterModels
		);

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
				profileUuid: profile.id,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.put<ISubscriptionProfile>(url, body);
	}

	public deleteProfile(
		subscriptionId: number,
		profileUuid: string
	): Observable<void> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_PROFILE_BY_ID');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
				profileUuid,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.delete<void>(url);
	}

	public getProfile(
		subscriptionId: number,
		profileUuid: string
	): Observable<ISubscriptionProfile> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_PROFILE_BY_ID');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
				profileUuid,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.get<ISubscriptionProfile>(url);
	}

	public getProjectProfileColumns(
		consumerProjectId: number,
		useCache: boolean
	): Observable<ReadonlyArray<IConsumedProfileColumn>> {
		const resourceUrl = this.generateResourceUrl(
			'PROJECT_DATALINK_PROFILE_COLUMNS'
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				authorizationId: consumerProjectId,
			},
			{
				project: this.projectId,
			}
		);
		return this.get<ReadonlyArray<IConsumedProfileColumn>>(
			url,
			CachePolicyEntity.build({
				policy: useCache ? CachePolicy.CacheOnly : CachePolicy.NetworkOnly,
				maxAgeInSeconds: 5 * 60,
				isCacheable: true,
			})
		);
	}

	public getSubscriptionColumnModels(
		subscriptionId: number,
		useCache = true
	): Observable<Array<IColumnTemplate>> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_COLUMN_TEMPLATES');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);

		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  });

		return this.get<Array<IColumnTemplate>>(url, cachePolicy);
	}

	public createSubscriptionColumnModel(
		subscriptionId: number,
		columnModel: ColumnModelItemEntity
	): Observable<IColumnTemplate> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_COLUMN_TEMPLATES');
		const body = this.generateColumnTemplatePayload(columnModel);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.post<IColumnTemplate>(url, body);
	}

	public updateColumnModel(
		subscriptionId: number,
		columnModel: ColumnModelItemEntity
	): Observable<IColumnTemplate> {
		const resourceUrl = this.generateResourceUrl(
			'DATALINKS_COLUMN_TEMPLATE_BY_ID'
		);
		const body = this.generateColumnTemplatePayload(columnModel);

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
				templateUuid: columnModel.id,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.put<IColumnTemplate>(url, body);
	}

	public deleteColumnModel(
		subscriptionId: number,
		columnModelUuid: string
	): Observable<void> {
		const resourceUrl = this.generateResourceUrl(
			'DATALINKS_COLUMN_TEMPLATE_BY_ID'
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
				templateUuid: columnModelUuid,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.delete<void>(url);
	}

	public getColumnModel(
		subscriptionId: number,
		columnModelUuid: string
	): Observable<IColumnTemplate> {
		const resourceUrl = this.generateResourceUrl(
			'DATALINKS_COLUMN_TEMPLATE_BY_ID'
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
				templateUuid: columnModelUuid,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.get<IColumnTemplate>(url);
	}

	public getSubscriptionFilterModels(
		subscriptionId: number,
		useCache = true
	): Observable<Array<IFilterTemplate>> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_FILTER_TEMPLATES');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);

		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  });

		return this.get<Array<IFilterTemplate>>(url, cachePolicy);
	}

	public createSubscriptionFilterModel(
		subscriptionId: number,
		filterModel: FilterModelItemEntity
	): Observable<IFilterTemplate> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_FILTER_TEMPLATES');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.post<IColumnTemplate>(
			url,
			this.generateFilterTemplatePayload(filterModel)
		);
	}

	public updateFilterModel(
		subscriptionId: number,
		filterModel: FilterModelItemEntity,
		filterRules: RuleFilterPayloadReq
	): Observable<IFilterTemplate> {
		const resourceUrl = this.generateResourceUrl(
			'DATALINKS_FILTER_TEMPLATE_BY_ID'
		);

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
				templateUuid: filterModel.id,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.put<IColumnTemplate>(url, {
			label: filterModel.label,
			description: filterModel.description,
			filter: filterRules,
		});
	}

	public deleteFilterModel(
		subscriptionId: number,
		filterModelUuid: string
	): Observable<void> {
		const resourceUrl = this.generateResourceUrl(
			'DATALINKS_FILTER_TEMPLATE_BY_ID'
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
				templateUuid: filterModelUuid,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.delete<void>(url);
	}

	public subscriptionSimulation(
		subscriptionId: number,
		dataSource: SimulationDataSource,
		profile: ProfileItemEntity | string,
		displayColumns: Array<string> = [],
		sortColumns: Array<SimulationSortColumnType> = []
	): Observable<ParsedSimulationResult> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_SIMULATE');

		let body: ISimulationPayload = {
			[camelToSnake('simulationDataSource')]:
				this.getSimulationDataSource(dataSource),
		};

		if (profile instanceof ProfileItemEntity) {
			body = {
				...body,
				[camelToSnake('columnTemplateUuid')]: profile.columnModelId,
				[camelToSnake('filterTemplateUuids')]: [...profile.filterModelIds],
			};
		} else {
			body = {
				...body,
				[camelToSnake('profileUuid')]: profile,
			};
		}

		if (displayColumns.length > 0) {
			body = {
				...body,
				[camelToSnake('displayColumnUuids')]: displayColumns,
			};
		}

		if (sortColumns.length > 0) {
			body = {
				...body,
				[camelToSnake('sortColumns')]: sortColumns.map<SimulationSortColumn>(
					(elem) => ({
						uuid: elem.uuid,
						direction: elem.order,
					})
				),
			};
		}

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.post<SimulationResult>(url, body).pipe(
			map((data) => {
				if ('errorResponse' in data || 'errorReponse' in data) {
					return {
						result: [],
						count: 0,
					};
				}
				const execData = data as SimulationResult;
				return {
					result: execData ? JSON.parse(execData.data as string) : [],
					count: execData?.count ?? 0,
				};
			})
		);
	}

	public initializeSubscription(
		subscriptionId: number,
		clientUuid: string
	): Observable<IJobTracking> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_SNAPSHOTS');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
				clientId: clientUuid,
			}
		);
		return this.http.post<IJobTracking>(url, {});
	}

	public updateSubscriptionData(
		subscriptionId: number,
		clientUuid: string
	): Observable<IJobTracking> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_SNAPSHOTS');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
				clientId: clientUuid,
			}
		);
		return this.http.put<IJobTracking>(url, {});
	}

	public clearSubscriptionData(subscriptionId: number): Observable<void> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_SNAPSHOTS');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.delete<void>(url);
	}

	public deactivateSubscription(subscriptionId: number): Observable<void> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_SUSPEND');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.put<void>(url, {});
	}

	public activateSubscription(subscriptionId: number): Observable<void> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_REACTIVATE');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.put<void>(url, {});
	}

	public getAllHistories(
		subscriptionId: number,
		useCache = true
	): Observable<Array<ISubscriptionHistory>> {
		const resourceUrl = this.generateResourceUrl('DATALINKS_HISTORY_LIST');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);

		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  });

		return this.get<Array<ISubscriptionHistory>>(url, cachePolicy);
	}

	public getConsumedProjects(
		subscriptionId: number,
		filterVersion: FilterVersion = FilterVersion.CurrentVersion
	): Observable<Array<ISubscriptionProjectModel>> {
		const resourceUrl = this.generateResourceUrl(
			'PROJECT_DATALINKS_FOR_PROFILE'
		);

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				versions: this.filterVersionMapping[filterVersion],
				project: this.projectId,
			}
		);

		return this.http.get<Array<ISubscriptionProjectModel>>(url);
	}

	public getSubscribableProjects(
		subscriptionId: number,
		useCache = true
	): Observable<ReadonlyArray<IProjectModel>> {
		const resourceUrl = this.generateResourceUrl(
			'DATALINKS_SUBSCRIBABLE_PROJECTS'
		);

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);

		return this.get<ReadonlyArray<IProjectModel>>(
			url,
			CachePolicyEntity.build({
				policy: useCache
					? CachePolicy.NetworkAndCache
					: CachePolicy.NetworkOnly,
				maxAgeInSeconds: 60,
				isCacheable: true,
			})
		);
	}

	public deleteConsumedProject(
		subscriptionId: number,
		projectSubscriptionId: number
	): Observable<void> {
		const resourceUrl = this.generateResourceUrl('PROJECT_DATALINK_BY_ID');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
				projectDataLinkId: projectSubscriptionId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.delete<void>(url);
	}

	public createNewConsumer(
		subscriptionId: number,
		profileUuid: string,
		consumerId: number
	): Observable<ISubscriptionProjectModel> {
		const resourceUrl = this.generateResourceUrl(
			'PROJECT_DATALINKS_FOR_PROFILE'
		);

		const body: IConsumerCreate = {
			[camelToSnake('targetProjectId')]: consumerId,
			[camelToSnake('dataLinkProfileUuid')]: profileUuid,
		};

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.post<ISubscriptionProjectModel>(url, body);
	}

	public updateConsumer(
		subscriptionId: number,
		profileUuid: string,
		projectUuid: string
	): Observable<ISubscriptionProjectModel> {
		const resourceUrl = this.generateResourceUrl('PROJECT_DATALINK_BY_ID');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
				projectDataLinkId: projectUuid,
			},
			{
				project: this.projectId,
				newProfileUuid: profileUuid,
			}
		);
		return this.http.put<ISubscriptionProjectModel>(url, {});
	}

	public updateProjectConsumeStatus(
		subscriptionId: number,
		projectSubscriptionId: number,
		isActive: boolean
	): Observable<void> {
		const resourceUrl = this.generateResourceUrl(
			isActive ? 'PROJECT_DATALINK_RESUME' : 'PROJECT_DATALINK_PAUSE'
		);

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
				projectDataLinkId: projectSubscriptionId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.put<void>(url, {});
	}

	public getSwitchableConsumedProjectsForSubscription(
		subscriptionId: number
	): Observable<ReadonlyArray<ISwitchableProjectModel>> {
		const resourceUrl = this.generateResourceUrl('DATALINK_SWITCHABLE');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.get<ReadonlyArray<ISwitchableProjectModel>>(url);
	}

	public getSwitchableConsumedProjects(): Observable<
		ReadonlyArray<ISwitchableProjectModel>
	> {
		const resourceUrl = this.generateResourceUrl(
			'PROJECT_DATALINKS_SWITCHABLE'
		);

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
			}
		);

		return this.http.get<ReadonlyArray<ISwitchableProjectModel>>(url);
	}

	public switchVersionForSubscriptionConsumedProjects(
		subscriptionId: number,
		consumedProjectIds: Array<number>
	): Observable<ISwitchVersionResponseModel> {
		const resourceUrl = this.generateResourceUrl('DATALINK_SWITCH');

		const body: VersionSwitchModel = {
			[camelToSnake('connectionIds')]: consumedProjectIds,
		};

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: subscriptionId,
			},
			{
				project: this.projectId,
			}
		);

		return this.http.post<ISwitchVersionResponseModel>(url, body);
	}

	public switchVersionForConsumedProjects(
		consumedProjectIds: Array<number>
	): Observable<ISwitchVersionResponseModel> {
		const resourceUrl = this.generateResourceUrl('PROJECT_DATALINKS_SWITCH');

		const body: VersionSwitchModel = {
			[camelToSnake('connectionIds')]: consumedProjectIds,
		};

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
			}
		);

		return this.http.post<ISwitchVersionResponseModel>(url, body);
	}

	public getSubscriptionPermission(
		metadataId: number,
		useCache = true
	): Observable<Array<ISubscriptionMemberAccess>> {
		const resourceUrl = this.generateResourceUrl('PERMISSIONS_BY_ELEMENT_USER');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				metadataId,
			},
			{
				project: this.projectId,
			}
		);

		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 60,
					isCacheable: true,
			  });

		return this.get<Array<ISubscriptionMemberAccess>>(url, cachePolicy);
	}

	public updateSubscriptionMembersAccess(
		metadataId: number,
		membersAccess: Array<MemberAccessEntity>,
		availablePermissions: ImMap<DcCoreElementType, Set<ElementPermission>>
	): Observable<void> {
		const resourceUrl = this.generateResourceUrl('PERMISSIONS_UPDATE_MATRIX');

		const subscriptionPermissions = availablePermissions.get(
			DcCoreElementType.DATALINK
		) as Set<ElementPermission>;

		const body: IMembersPermissionsUpdate = {
			[camelToSnake('metadataId')]: metadataId,
			members: membersAccess.map((member) => ({
				[camelToSnake('userId')]: member.memberUuid,
				[camelToSnake('isGroup')]: member.isGroup,
				permissions: Object.fromEntries(
					[...subscriptionPermissions].map((permission) => [
						permission,
						member.permissions.has(permission),
					])
				),
			})),
		};

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				projectId: this.projectId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.post<void>(url, body);
	}

	private generateSubscriptionPayloadForSave(
		datablockId: number,
		internalMetadata: InternalMetadataEntity,
		publicMetadata: PublicMetadataEntity,
		columns: ImMap<string, ColumnConfigItemEntity>,
		profile: ProfileItemEntity,
		columnModel: ColumnModelItemEntity,
		filterModels: Array<FilterModelItemEntity>
	): ISubscriptionCreate {
		const subscriptionColumns =
			this.generateSubscriptionColumnsPayload(columns);

		const profileMetadata: IProfileMetadata = {
			label: profile.label,
			description: profile.description,
		};

		return {
			[camelToSnake('datablockId')]: datablockId,
			metadata: this.generateInternalMetadataPayload(internalMetadata),
			[camelToSnake('publicMetadata')]:
				this.generatePublicMetadataPayload(publicMetadata),
			[camelToSnake('visibleInCatalog')]: true,
			columns: subscriptionColumns,
			profile: profileMetadata,
			[camelToSnake('columnTemplate')]:
				this.generateColumnTemplatePayload(columnModel),
			[camelToSnake('filterTemplates')]: filterModels.map((f) =>
				this.generateFilterTemplatePayload(f)
			),
		};
	}

	private generateSubscriptionColumnsPayload(
		columns: ImMap<string, ColumnConfigItemEntity>
	): Array<ISubscriptionColumnConfig> {
		return columns
			.toList()
			.toArray()
			.map<ISubscriptionColumnConfig>((col) => ({
				uuid: col.id,
				label: col.label,
				alias: col.alias,
				description: col.description,
				active: col.isSelectedInSource,
			}));
	}

	private generateColumnTemplatePayload(
		columnModel: ColumnModelItemEntity
	): IColumnTemplate {
		const columns = columnModel.columns
			.toArray()
			.map<ISubscriptionProfileColumn>((col) => ({
				uuid: col.id,
				hash: col.isHashed,
			}));

		return {
			label: columnModel.label,
			description: columnModel.description,
			columns,
		};
	}

	private generateFilterTemplatePayload(
		filterModel: FilterModelItemEntity
	): FilterPayloadReq {
		return {
			label: filterModel.label,
			description: filterModel.description,
			filter: this.ruleMapperService.toBackendGroup(
				filterModel.root,
				new Map()
			),
		};
	}

	private generateInternalMetadataPayload(
		metadata: InternalMetadataEntity
	): IDcMetadataResponse {
		const tags = metadata.tags.toArray().map((t) => ({
			id: t.id !== -1 ? t.id : undefined,
			code: t.label,
			color: t.color,
			position: t.position,
		}));

		return {
			id: metadata.id,
			label: metadata.label,
			description: metadata.description,
			['icon_id']: metadata.picId ?? -1,
			tags,
			actif: true, // to verify!
		};
	}

	private generatePublicMetadataPayload(
		metadata: PublicMetadataEntity
	): IPublicMetadata {
		return {
			title: metadata.title,
			details: metadata.description,
			keywords: metadata.keywords.toArray().map((keyword) => keyword.label),
		};
	}

	private generateSubscriptionProfilePayload(
		profile: ProfileItemEntity,
		columnModel: ColumnModelItemEntity | string,
		filterModels: Array<FilterModelItemEntity | string>
	): ISubscriptionProfile {
		return {
			label: profile.label,
			description: profile.description,
			[camelToSnake('columnTemplate')]:
				columnModel instanceof ColumnModelItemEntity
					? this.generateColumnTemplatePayload(columnModel)
					: {
							uuid: columnModel,
					  },
			[camelToSnake('filterTemplates')]: filterModels.map((f) =>
				f instanceof FilterModelItemEntity
					? this.generateFilterTemplatePayload(f)
					: {
							uuid: f,
					  }
			),
		};
	}

	private getSimulationDataSource(
		dataSource: SimulationDataSource
	): SimulationDataSourceEnum {
		switch (dataSource) {
			case 'DATABLOCK':
				return SimulationDataSourceEnum.DATABLOCK;
			case 'PUBLISH_DATA':
				return SimulationDataSourceEnum.SUBSCRIPTION;
			default:
				throw new Error(`Unrecognized data source: ${dataSource}`);
		}
	}

	private getTotal(
		response: HttpResponse<ReadonlyArray<ISubscriptionResponse>>
	): number {
		return Number.parseInt(response.headers.get('x-total-count') ?? '0', 10);
	}
}
